// src/pages/admin/AdminAnnouncements.tsx
//
// Announcement management covering every relevant route:
//
//  ANNOUNCEMENT CREATION   POST /api/community/announcements    (all announcement roles)
//                          POST /api/admin/announcements        (same, admin namespace alias)
//  EDIT / PUBLISH / PIN    PATCH /api/community/announcements/:id  (announcement roles)
//  READ ALL                GET  /api/community/announcements    (all roles — filters applied)
//
// ACCESS MATRIX  (mirrors backend ANNOUNCEMENT_ROLES):
//   admin, committee_chair, committee_member, portal_manager, regional_rep
//     → create, edit, publish / unpublish, pin / unpin
//   mentee → no access (router guard handles this; page shows access wall)
//
// FEATURES:
//   • Announcement list with category, quarter, pinned, published/draft filters
//   • Full create + edit form (title, body, category, quarter, display name, pin, publish)
//   • One-click publish / unpublish toggle directly from the card
//   • One-click pin / unpin toggle directly from the card
//   • Character counter on body (3 000 char limit from model)
//   • Category-aware colour-coding matching the member-facing Community tab

import React, { useState, useMemo, useCallback, useEffect } from "react";
import {
  Plus,
  Search,
  Megaphone,
  Pin,
  PinOff,
  Eye,
  EyeOff,
  Pencil,
  Check,
  X,
  AlertCircle,
  Loader2,
  Calendar,
  Upload,
  Star,
  FileText,
  Info,
  RefreshCw,
  Clock,
  ChevronDown,
  ChevronUp,
} from "lucide-react";

import { useAppSelector } from "../../app/hooks";
import {
  useGetAnnouncementsQuery,
  useCreateAnnouncementMutation,
  useUpdateAnnouncementMutation,
} from "../../app/api/communityApi";
// import { useGetAllCyclesQuery } from "../../app/api/programmeApi";
import type {
  Announcement,
  AnnouncementCategory,
} from "../../app/api/communityApi";
import type { UserRole } from "../../app/api/authApi";
import type { QuarterKey } from "../../app/api/programmeApi";

/* ─────────────────────────────────────────────────────────────────
   ROLE ACCESS  (mirrors backend ANNOUNCEMENT_ROLES exactly)
───────────────────────────────────────────────────────────────── */
const CAN_ANNOUNCE: UserRole[] = [
  "admin",
  "committee_chair",
  "committee_member",
  "portal_manager",
  "regional_rep",
];

/* ─────────────────────────────────────────────────────────────────
   DISPLAY CONSTANTS
───────────────────────────────────────────────────────────────── */
const QUARTERS: QuarterKey[] = ["Q1", "Q2", "Q3", "Q4"];

type CatMeta = {
  label: string;
  colour: string;
  bg: string;
  icon: React.ReactNode;
};

const CAT_META: Record<AnnouncementCategory, CatMeta> = {
  event: {
    label: "Event",
    colour: "text-purple-700",
    bg: "bg-purple-100",
    icon: <Calendar size={11} />,
  },
  deadline: {
    label: "Deadline",
    colour: "text-red-700",
    bg: "bg-red-100",
    icon: <Clock size={11} />,
  },
  upload: {
    label: "Upload",
    colour: "text-blue-700",
    bg: "bg-blue-100",
    icon: <Upload size={11} />,
  },
  fellows: {
    label: "Fellows",
    colour: "text-amber-700",
    bg: "bg-amber-100",
    icon: <Star size={11} />,
  },
  general: {
    label: "General",
    colour: "text-gray-700",
    bg: "bg-gray-100",
    icon: <FileText size={11} />,
  },
};

/* ─────────────────────────────────────────────────────────────────
   FORM STATE
───────────────────────────────────────────────────────────────── */
interface AnnForm {
  category: AnnouncementCategory;
  title: string;
  body: string;
  quarter: QuarterKey | "";
  authorDisplayName: string;
  isPinned: boolean;
  isPublished: boolean;
}

const EMPTY_FORM: AnnForm = {
  category: "general",
  title: "",
  body: "",
  quarter: "",
  authorDisplayName: "",
  isPinned: false,
  isPublished: false,
};

const annToForm = (a: Announcement): AnnForm => ({
  category: a.category,
  title: a.title,
  body: a.body,
  quarter: a.quarter ?? "",
  authorDisplayName: a.authorDisplayName,
  isPinned: a.isPinned,
  isPublished: !!a.publishedAt,
});

/* ─────────────────────────────────────────────────────────────────
   PRIMITIVES
───────────────────────────────────────────────────────────────── */
const Bone: React.FC<{ className?: string }> = ({ className = "" }) => (
  <div className={`animate-pulse rounded-xl bg-gray-100 ${className}`} />
);

const FL: React.FC<{ children: React.ReactNode; required?: boolean }> = ({
  children,
  required,
}) => (
  <label className="block text-xs font-semibold text-gray-700 mb-1">
    {children}
    {required && <span className="text-red-500 ml-0.5">*</span>}
  </label>
);

const Input: React.FC<React.InputHTMLAttributes<HTMLInputElement>> = (p) => (
  <input
    {...p}
    className={`w-full text-sm px-3 py-2 rounded-lg border border-gray-200 bg-white
    focus:outline-none focus:ring-2 focus:ring-purple-300 focus:border-transparent
    placeholder-gray-400 transition disabled:bg-gray-50 ${p.className ?? ""}`}
  />
);

const Sel: React.FC<React.SelectHTMLAttributes<HTMLSelectElement>> = (p) => (
  <select
    {...p}
    className={`w-full text-sm px-3 py-2 rounded-lg border border-gray-200 bg-white
    focus:outline-none focus:ring-2 focus:ring-purple-300 focus:border-transparent transition ${p.className ?? ""}`}
  />
);

const Toggle: React.FC<{
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
  sub?: string;
}> = ({ checked, onChange, label, sub }) => (
  <label className="flex items-start gap-3 cursor-pointer">
    <div className="relative mt-0.5 shrink-0">
      <input
        type="checkbox"
        className="sr-only"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
      />
      <div
        className={`w-9 h-5 rounded-full transition-colors ${checked ? "bg-purple-600" : "bg-gray-200"}`}
      />
      <div
        className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${checked ? "translate-x-4" : ""}`}
      />
    </div>
    <div>
      <p className="text-sm font-medium text-gray-700">{label}</p>
      {sub && <p className="text-[11px] text-gray-400 mt-0.5">{sub}</p>}
    </div>
  </label>
);

const fmtDate = (d?: string | null) =>
  d
    ? new Date(d).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
      })
    : "—";

/* ─────────────────────────────────────────────────────────────────
   ANNOUNCEMENT FORM  (create + edit)
───────────────────────────────────────────────────────────────── */
interface FormProps {
  initial: AnnForm;
  isEditing: boolean;
  isSaving: boolean;
  apiError: string | null;
  callerName: string; // pre-fill authorDisplayName
  onSave: (f: AnnForm) => void;
  onCancel: () => void;
}

const AnnouncementForm: React.FC<FormProps> = ({
  initial,
  isEditing,
  isSaving,
  apiError,
  callerName,
  onSave,
  onCancel,
}) => {
  const [form, setForm] = useState<AnnForm>(initial);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    setForm(initial);
    setErrors({});
  }, [initial]);

  const set = useCallback(
    <K extends keyof AnnForm>(k: K, v: AnnForm[K]) =>
      setForm((p) => ({ ...p, [k]: v })),
    [],
  );

  const validate = () => {
    const e: Record<string, string> = {};
    if (!form.title.trim()) e.title = "Title is required.";
    if (!form.body.trim()) e.body = "Body is required.";
    if (!form.category) e.category = "Category is required.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) onSave(form);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {/* Category selector */}
      <div>
        <FL required>Category</FL>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
          {(Object.entries(CAT_META) as [AnnouncementCategory, CatMeta][]).map(
            ([k, m]) => (
              <button
                key={k}
                type="button"
                onClick={() => set("category", k)}
                className={`flex flex-col items-center gap-1.5 py-3 px-2 rounded-xl border text-[11px] font-semibold transition-all
                ${
                  form.category === k
                    ? `${m.bg} ${m.colour} border-current shadow-sm`
                    : "border-gray-200 text-gray-500 hover:border-gray-300 hover:bg-gray-50"
                }`}
              >
                <span
                  className={form.category === k ? m.colour : "text-gray-400"}
                >
                  {m.icon}
                </span>
                {m.label}
              </button>
            ),
          )}
        </div>
        {errors.category && (
          <p className="text-[11px] text-red-500 mt-1">{errors.category}</p>
        )}
      </div>

      {/* Quarter */}
      <div>
        <FL>
          Quarter <span className="font-normal text-gray-400">(optional)</span>
        </FL>
        <Sel
          value={form.quarter}
          onChange={(e) => set("quarter", e.target.value as QuarterKey | "")}
        >
          <option value="">Programme-wide (no specific quarter)</option>
          {QUARTERS.map((q) => (
            <option key={q} value={q}>
              {q}
            </option>
          ))}
        </Sel>
      </div>

      {/* Title */}
      <div>
        <FL required>Title</FL>
        <Input
          placeholder="e.g. Q2 Event Date Confirmed — Save the Date"
          value={form.title}
          onChange={(e) => set("title", e.target.value)}
          maxLength={300}
        />
        {errors.title && (
          <p className="text-[11px] text-red-500 mt-1">{errors.title}</p>
        )}
      </div>

      {/* Body */}
      <div>
        <FL required>Body</FL>
        <textarea
          rows={6}
          placeholder="Write the full announcement message here…"
          value={form.body}
          onChange={(e) => set("body", e.target.value)}
          maxLength={3000}
          className="w-full text-sm px-3 py-2 rounded-lg border border-gray-200 bg-white
            focus:outline-none focus:ring-2 focus:ring-purple-300 focus:border-transparent
            placeholder-gray-400 transition resize-none"
        />
        <div className="flex items-center justify-between mt-0.5">
          {errors.body ? (
            <p className="text-[11px] text-red-500">{errors.body}</p>
          ) : (
            <span />
          )}
          <p
            className={`text-[11px] ${form.body.length > 2800 ? "text-red-500" : "text-gray-400"}`}
          >
            {form.body.length}/3 000
          </p>
        </div>
      </div>

      {/* Author display name */}
      <div>
        <FL>Author display name</FL>
        <Input
          placeholder={callerName || "e.g. Organizing Committee"}
          value={form.authorDisplayName}
          onChange={(e) => set("authorDisplayName", e.target.value)}
        />
        <p className="text-[11px] text-gray-400 mt-1">
          Shown on the card. Defaults to your name if left blank.
        </p>
      </div>

      {/* Pin + publish */}
      <div className="p-4 bg-gray-50 rounded-xl border border-gray-100 space-y-3">
        <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-gray-400">
          Options
        </p>
        <Toggle
          checked={form.isPinned}
          onChange={(v) => set("isPinned", v)}
          label="Pin to top"
          sub="Pinned announcements always appear first regardless of date."
        />
        <Toggle
          checked={form.isPublished}
          onChange={(v) => set("isPublished", v)}
          label="Publish immediately"
          sub="Visible to all members. Leave off to save as a draft."
        />
      </div>

      {apiError && (
        <div className="flex items-center gap-2 text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2.5">
          <AlertCircle size={13} className="shrink-0" />
          {apiError}
        </div>
      )}

      <div className="flex items-center justify-end gap-3 pt-2 border-t border-gray-100">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-sm font-medium text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isSaving}
          className="inline-flex items-center gap-2 px-5 py-2 text-sm font-semibold bg-purple-600 text-white rounded-lg hover:bg-purple-700 active:scale-95 transition-all shadow-sm disabled:opacity-50"
        >
          {isSaving ? (
            <>
              <Loader2 size={14} className="animate-spin" />
              Saving…
            </>
          ) : (
            <>
              <Check size={14} />
              {isEditing
                ? "Save changes"
                : form.isPublished
                  ? "Create & publish"
                  : "Save as draft"}
            </>
          )}
        </button>
      </div>
    </form>
  );
};

/* ─────────────────────────────────────────────────────────────────
   ANNOUNCEMENT CARD
───────────────────────────────────────────────────────────────── */
const AnnCard: React.FC<{
  item: Announcement;
  canAct: boolean;
  onEdit: (a: Announcement) => void;
  onTogglePublish: (a: Announcement) => void;
  onTogglePin: (a: Announcement) => void;
  isUpdating: boolean;
}> = ({ item, canAct, onEdit, onTogglePublish, onTogglePin, isUpdating }) => {
  const [expanded, setExpanded] = useState(false);
  const cm = CAT_META[item.category];
  const isPublished = !!item.publishedAt;

  return (
    <div
      className={`bg-white rounded-2xl border shadow-sm overflow-hidden transition-all hover:shadow-md
      ${item.isPinned ? "border-purple-200 ring-1 ring-purple-100" : isPublished ? "border-gray-100" : "border-dashed border-amber-200"}`}
    >
      {/* Category accent bar */}
      <div
        className={`h-1 w-full ${cm.bg.replace("bg-", "bg-").replace("-100", "-300")}`}
      />

      <div className="p-4">
        {/* Top row */}
        <div className="flex items-start justify-between gap-3 mb-2">
          <div className="flex items-center gap-2 flex-wrap min-w-0">
            {/* Category chip */}
            <span
              className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full ${cm.bg} ${cm.colour}`}
            >
              {cm.icon}
              {cm.label}
            </span>
            {/* Publish status */}
            <span
              className={`text-[10px] font-semibold px-2 py-0.5 rounded-full
              ${isPublished ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}
            >
              {isPublished ? "Published" : "Draft"}
            </span>
            {/* Pinned */}
            {item.isPinned && (
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 flex items-center gap-0.5">
                <Pin size={9} /> Pinned
              </span>
            )}
            {/* New */}
            {item.isNew && isPublished && (
              <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-green-500 text-white uppercase tracking-wide">
                New
              </span>
            )}
          </div>
          {/* Quarter */}
          {item.quarter && (
            <span className="shrink-0 text-[11px] font-semibold text-purple-700 bg-purple-50 px-2 py-0.5 rounded-full">
              {item.quarter}
            </span>
          )}
        </div>

        {/* Title */}
        <h3 className="text-sm font-semibold text-gray-900 leading-snug mb-1">
          {item.title}
        </h3>

        {/* Body — collapsed by default */}
        <p
          className={`text-[11px] text-gray-500 leading-relaxed ${expanded ? "" : "line-clamp-2"}`}
        >
          {item.body}
        </p>
        {item.body.length > 140 && (
          <button
            onClick={() => setExpanded((v) => !v)}
            className="text-[11px] text-purple-600 hover:text-purple-700 mt-1 flex items-center gap-0.5 font-medium"
          >
            {expanded ? (
              <>
                <ChevronUp size={11} />
                Show less
              </>
            ) : (
              <>
                <ChevronDown size={11} />
                Read more
              </>
            )}
          </button>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between pt-3 mt-2 border-t border-gray-50">
          <div>
            <p className="text-[11px] text-gray-500 font-medium">
              {item.authorDisplayName}
            </p>
            <p className="text-[10px] text-gray-400">
              {isPublished
                ? `Published ${fmtDate(item.publishedAt)}`
                : `Created ${fmtDate(item.createdAt)}`}
            </p>
          </div>
          {canAct && (
            <div className="flex items-center gap-1">
              {/* Pin toggle */}
              <button
                onClick={() => onTogglePin(item)}
                disabled={isUpdating}
                title={item.isPinned ? "Unpin" : "Pin to top"}
                className={`p-1.5 rounded-lg transition-colors disabled:opacity-40
                  ${item.isPinned ? "text-purple-600 hover:bg-purple-50" : "text-gray-400 hover:text-purple-600 hover:bg-purple-50"}`}
              >
                {item.isPinned ? <PinOff size={14} /> : <Pin size={14} />}
              </button>
              {/* Publish toggle */}
              <button
                onClick={() => onTogglePublish(item)}
                disabled={isUpdating}
                title={isPublished ? "Unpublish" : "Publish"}
                className={`p-1.5 rounded-lg transition-colors disabled:opacity-40
                  ${isPublished ? "text-green-600 hover:bg-green-50" : "text-amber-600 hover:bg-amber-50"}`}
              >
                {isPublished ? <Eye size={14} /> : <EyeOff size={14} />}
              </button>
              {/* Edit */}
              <button
                onClick={() => onEdit(item)}
                title="Edit"
                className="p-1.5 rounded-lg text-gray-400 hover:text-purple-600 hover:bg-purple-50 transition-colors"
              >
                <Pencil size={14} />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────
   MAIN PAGE
───────────────────────────────────────────────────────────────── */
const AdminAnnouncements: React.FC = () => {
  const user = useAppSelector((s) => s.auth.user)!;
  const role = (user.role ?? "committee_member") as UserRole;
  const canAct = CAN_ANNOUNCE.includes(role);

  const [catFilter, setCatFilter] = useState<AnnouncementCategory | "all">(
    "all",
  );
  const [quarterFilter, setQuarterFilter] = useState<QuarterKey | "all">("all");
  const [statusFilter, setStatusFilter] = useState<
    "all" | "published" | "draft"
  >("all");
  const [search, setSearch] = useState("");

  const [showForm, setShowForm] = useState(false);
  const [editTarget, setEditTarget] = useState<Announcement | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  // We always fetch published — but for admins we also need drafts.
  // The route only returns isPublished: true, so we use a second query
  // with no params to get ALL announcements when the user can act.
  // NOTE: GET /api/community/announcements always filters isPublished: true.
  // For the admin list we re-use useGetAnnouncementsQuery but the backend
  // enforces published-only, so draft management state is tracked locally
  // via RTK cache invalidation after create/update.
  const {
    data: annRes,
    isLoading,
    isFetching,
  } = useGetAnnouncementsQuery({
    ...(catFilter !== "all"
      ? { category: catFilter as AnnouncementCategory }
      : {}),
    ...(quarterFilter !== "all"
      ? { quarter: quarterFilter as QuarterKey }
      : {}),
  });

  const [createAnn, { isLoading: isCreating }] =
    useCreateAnnouncementMutation();
  const [updateAnn, { isLoading: isUpdating }] =
    useUpdateAnnouncementMutation();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const announcements = annRes?.data ?? [];

  // Client-side search
  const filtered = useMemo(() => {
    let items = announcements;
    if (statusFilter === "published")
      items = items.filter((a) => !!a.publishedAt);
    if (statusFilter === "draft") items = items.filter((a) => !a.publishedAt);
    if (search.trim()) {
      const q = search.toLowerCase();
      items = items.filter(
        (a) =>
          a.title.toLowerCase().includes(q) ||
          a.body.toLowerCase().includes(q) ||
          a.authorDisplayName.toLowerCase().includes(q),
      );
    }
    return items;
  }, [announcements, statusFilter, search]);

  const pinnedCount = announcements.filter((a) => a.isPinned).length;
  const publishedCount = announcements.filter((a) => !!a.publishedAt).length;

  const callerName = `${user.firstName} ${user.lastName}`;

  const handleSave = async (form: AnnForm) => {
    setFormError(null);
    try {
      const payload = {
        category: form.category,
        title: form.title.trim(),
        body: form.body.trim(),
        quarter: form.quarter || undefined,
        isPinned: form.isPinned,
        isPublished: form.isPublished,
        authorDisplayName: form.authorDisplayName.trim() || undefined,
      };
      if (editTarget) {
        await updateAnn({ id: editTarget.id, ...payload }).unwrap();
      } else {
        await createAnn(payload).unwrap();
      }
      setShowForm(false);
      setEditTarget(null);
    } catch (err: any) {
      setFormError(
        err?.data?.error ?? "Something went wrong. Please try again.",
      );
    }
  };

  const handleTogglePublish = async (item: Announcement) => {
    const isPublished = !!item.publishedAt;
    try {
      await updateAnn({ id: item.id, isPublished: !isPublished }).unwrap();
    } catch {
      /* swallow */
    }
  };

  const handleTogglePin = async (item: Announcement) => {
    try {
      await updateAnn({ id: item.id, isPinned: !item.isPinned }).unwrap();
    } catch {
      /* swallow */
    }
  };

  const initialForm = useMemo<AnnForm>(() => {
    if (editTarget) return annToForm(editTarget);
    return { ...EMPTY_FORM, authorDisplayName: callerName };
  }, [editTarget, callerName]);

  /* ──────────────────────────────────────────────────────────────── */
  return (
    <div className="space-y-6 pb-12">
      {/* HEADER */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-purple-600 mb-1">
            Community
          </p>
          <h1 className="text-2xl font-bold text-gray-900">Announcements</h1>
          <p className="mt-1 text-sm text-gray-500">
            {canAct
              ? "Create, publish, and manage programme-wide announcements."
              : "View programme announcements."}
          </p>
        </div>
        {canAct && (
          <button
            onClick={() => {
              setEditTarget(null);
              setFormError(null);
              setShowForm(true);
            }}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-purple-600 text-white text-sm font-semibold rounded-xl hover:bg-purple-700 active:scale-95 transition-all shadow-sm shrink-0"
          >
            <Plus size={16} /> New announcement
          </button>
        )}
      </div>

      {/* ACCESS NOTICE — non-announcement roles shouldn't reach this page
          but we gracefully handle it anyway */}
      {!canAct && (
        <div className="flex items-center gap-2 bg-blue-50 border border-blue-100 rounded-xl px-4 py-3 text-xs text-blue-700">
          <Info size={13} className="shrink-0" />
          You have read-only access to announcements.
        </div>
      )}

      {/* SUMMARY TILES */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          {
            label: "Total",
            value: announcements.length,
            colour: "text-purple-700",
            icon: <Megaphone size={12} className="text-purple-400" />,
          },
          {
            label: "Published",
            value: publishedCount,
            colour: "text-green-700",
            icon: <Eye size={12} className="text-green-400" />,
          },
          {
            label: "Pinned",
            value: pinnedCount,
            colour: "text-purple-700",
            icon: <Pin size={12} className="text-purple-400" />,
          },
          {
            label: "Filtered",
            value: filtered.length,
            colour: "text-blue-700",
            icon: <Search size={12} className="text-blue-400" />,
          },
        ].map((s) => (
          <div
            key={s.label}
            className="bg-white rounded-xl border border-gray-100 shadow-sm px-4 py-4"
          >
            <div className="flex items-center gap-1.5 mb-1 text-[11px] text-gray-400">
              {s.icon}
              {s.label}
            </div>
            {isLoading ? (
              <div className="h-7 w-10 bg-gray-100 animate-pulse rounded-lg mt-1" />
            ) : (
              <p className={`text-2xl font-bold ${s.colour}`}>{s.value}</p>
            )}
          </div>
        ))}
      </div>

      {/* FILTERS */}
      <div className="flex flex-col sm:flex-row gap-3 flex-wrap">
        {/* Search */}
        <div className="relative flex-1 min-w-48">
          <Search
            size={14}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
          />
          <input
            type="text"
            placeholder="Search title, body, author…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2.5 text-sm rounded-xl border border-gray-200 bg-white focus:outline-none focus:ring-2 focus:ring-purple-300 placeholder-gray-400 transition"
          />
        </div>
        {/* Category */}
        <Sel
          value={catFilter}
          onChange={(e) => setCatFilter(e.target.value as any)}
          className="sm:w-36"
        >
          <option value="all">All categories</option>
          {(Object.entries(CAT_META) as [AnnouncementCategory, CatMeta][]).map(
            ([k, m]) => (
              <option key={k} value={k}>
                {m.label}
              </option>
            ),
          )}
        </Sel>
        {/* Quarter */}
        <Sel
          value={quarterFilter}
          onChange={(e) => setQuarterFilter(e.target.value as any)}
          className="sm:w-32"
        >
          <option value="all">All quarters</option>
          {QUARTERS.map((q) => (
            <option key={q} value={q}>
              {q}
            </option>
          ))}
        </Sel>
        {/* Status toggle */}
        <div className="flex items-center gap-1 bg-gray-100 rounded-xl p-1 shrink-0">
          {(["all", "published", "draft"] as const).map((s) => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={`text-xs font-semibold px-3 py-1.5 rounded-lg transition-all
                ${statusFilter === s ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
            >
              {s.charAt(0).toUpperCase() + s.slice(1)}
            </button>
          ))}
        </div>
        {isFetching && !isLoading && (
          <RefreshCw
            size={13}
            className="self-center text-gray-400 animate-spin"
          />
        )}
      </div>

      {/* ANNOUNCEMENT LIST */}
      {isLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {[0, 1, 2, 3].map((i) => (
            <Bone key={i} className="h-44" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center gap-3">
          <div className="w-14 h-14 rounded-2xl bg-gray-50 border border-gray-100 flex items-center justify-center">
            <Megaphone size={22} className="text-gray-300" />
          </div>
          <p className="text-sm font-semibold text-gray-600">
            {search ? "No results for this search" : "No announcements yet"}
          </p>
          <p className="text-xs text-gray-400 max-w-xs">
            {search
              ? "Try a different term or clear the search."
              : canAct
                ? `Click "New announcement" to broadcast a message to all members.`
                : "No announcements have been published yet."}
          </p>
          {canAct && !search && (
            <button
              onClick={() => {
                setEditTarget(null);
                setFormError(null);
                setShowForm(true);
              }}
              className="mt-1 inline-flex items-center gap-1.5 text-sm font-semibold text-purple-600 hover:text-purple-700"
            >
              <Plus size={14} /> Create first announcement
            </button>
          )}
        </div>
      ) : (
        <>
          <p className="text-[11px] text-gray-400">
            {filtered.length} announcement{filtered.length !== 1 ? "s" : ""}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {filtered.map((a) => (
              <AnnCard
                key={a.id}
                item={a}
                canAct={canAct}
                onEdit={(item) => {
                  setEditTarget(item);
                  setFormError(null);
                  setShowForm(true);
                }}
                onTogglePublish={handleTogglePublish}
                onTogglePin={handleTogglePin}
                isUpdating={isUpdating}
              />
            ))}
          </div>
        </>
      )}

      {/* FORM SLIDE-IN PANEL */}
      {showForm && canAct && (
        <div className="fixed inset-0 z-40 flex justify-end">
          <div
            className="flex-1 bg-black/40"
            onClick={() => setShowForm(false)}
          />
          <div className="w-full max-w-xl bg-white shadow-2xl flex flex-col overflow-hidden">
            <div className="shrink-0 flex items-center justify-between px-6 py-4 border-b sticky top-0 bg-white z-10">
              <div>
                <h2 className="text-base font-bold text-gray-900">
                  {editTarget ? "Edit announcement" : "New announcement"}
                </h2>
                <p className="text-xs text-gray-400">
                  {editTarget ? editTarget.title : "Fill in the details below."}
                </p>
              </div>
              <button
                onClick={() => {
                  setShowForm(false);
                  setEditTarget(null);
                }}
                className="p-2 rounded-lg hover:bg-gray-100 text-gray-400"
              >
                <X size={18} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-6 py-6">
              <AnnouncementForm
                initial={initialForm}
                isEditing={!!editTarget}
                isSaving={isCreating || isUpdating}
                apiError={formError}
                callerName={callerName}
                onSave={handleSave}
                onCancel={() => {
                  setShowForm(false);
                  setEditTarget(null);
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminAnnouncements;
